/* pricing.ts — the on-demand compositional pricing vocabulary (ADR 0009).

   Mirrors bigdayz.new/response_generation_with_graph/content_request_pricing.py:
   the same photo tiers, video categories and minute-buckets, so the matrix the
   operator authors here is exactly the one the branch prices with. Money is a pure
   function of the Classification of the content actually selected — never stated by
   the model — so these tables are the whole pricing surface. */

import type {
  ContentPricingRules, PricePair, SimplePricingInputs, ShapeContentOverride,
  VideoTierKey, TierRange,
} from '../../../api/client';

// Photo nudity tiers (priced per tier).
export const PHOTO_TIERS = ['non_nude', 'nude', 'explicit'] as const;
export type PhotoTier = (typeof PHOTO_TIERS)[number];

// Video categories (priced per category × minute-bucket cell).
export const VIDEO_CATEGORIES = [
  'non_nude',
  'life',
  'striptease',
  'solo',
  'oral',
  'masturbation',
  'gg',
  'bg',
  'anal',
  'hardcore',
] as const;
export type VideoCategory = (typeof VIDEO_CATEGORIES)[number];

// Fine minute-buckets (independent of the coarse Classification duration_bucket).
export const MINUTE_BUCKETS = [
  '<1',
  '1-2',
  '2-3',
  '3-4',
  '4-5',
  '5-6',
  '6-7',
  '7-8',
  '8-9',
  '9-10',
  '>10',
] as const;
export type MinuteBucket = (typeof MINUTE_BUCKETS)[number];

export const PHOTO_TIER_LABEL: Record<PhotoTier, string> = {
  non_nude: 'Non-nude',
  nude: 'Nude',
  explicit: 'Explicit',
};

export const VIDEO_CATEGORY_LABEL: Record<VideoCategory, string> = {
  non_nude: 'Non-nude',
  life: 'Lifestyle',
  striptease: 'Striptease',
  solo: 'Solo',
  oral: 'Oral (POV)',
  masturbation: 'Masturbation',
  gg: 'Girl / girl',
  bg: 'Boy / girl',
  anal: 'Anal',
  hardcore: 'Hardcore',
};

/** Read a `[default, floor]` cell, coercing to a definite pair (nulls → 0). */
export function readPair(cell: PricePair | undefined | null): [number | null, number | null] {
  if (!cell) return [null, null];
  return [cell[0] ?? null, cell[1] ?? null];
}

export function photoCell(rules: ContentPricingRules, tier: PhotoTier): PricePair {
  return rules.photo?.[tier] ?? [null, null];
}

export function videoCell(
  rules: ContentPricingRules,
  cat: VideoCategory,
  bucket: MinuteBucket,
): PricePair {
  return rules.video?.[cat]?.[bucket] ?? [null, null];
}

/** Immutably set a photo tier's default or floor. */
export function setPhotoCell(
  rules: ContentPricingRules,
  tier: PhotoTier,
  idx: 0 | 1,
  value: number | null,
): ContentPricingRules {
  const photo = { ...(rules.photo ?? {}) };
  const pair: PricePair = [...(photo[tier] ?? [null, null])] as PricePair;
  pair[idx] = value;
  photo[tier] = pair;
  return { ...rules, photo };
}

/** Immutably set a video cell's default or floor. */
export function setVideoCell(
  rules: ContentPricingRules,
  cat: VideoCategory,
  bucket: MinuteBucket,
  idx: 0 | 1,
  value: number | null,
): ContentPricingRules {
  const video = { ...(rules.video ?? {}) };
  const cats = { ...(video[cat] ?? {}) };
  const pair: PricePair = [...(cats[bucket] ?? [null, null])] as PricePair;
  pair[idx] = value;
  cats[bucket] = pair;
  video[cat] = cats;
  return { ...rules, video };
}

/** True when a category has at least one non-zero priced bucket. */
export function categoryHasPricing(rules: ContentPricingRules, cat: VideoCategory): boolean {
  const cells = rules.video?.[cat];
  if (!cells) return false;
  return Object.values(cells).some((p) => (p?.[0] ?? 0) > 0);
}

/** True when a matrix has any photo or video cell authored (an unconfigured policy
 * reads as `{}` and should fall back to {@link defaultPricingRules}). */
export function hasAnyPricing(rules: ContentPricingRules | undefined | null): boolean {
  if (!rules) return false;
  return (
    Object.keys(rules.photo ?? {}).length > 0 ||
    Object.keys(rules.video ?? {}).length > 0
  );
}

/**
 * The canonical default matrix — a verbatim mirror of bigdayz.new's built-in
 * `DEFAULT_PRICING_RULES` (response_generation_with_graph/content_request_pricing.py),
 * the fail-safe the branch prices with when a cell is missing. Every account starts
 * from exactly this so a policy is never blank and every classified archetype
 * (all photo tiers, all video categories × every minute-bucket) has an explicit price.
 *
 * bigdayz.new prices video per category flat across length; we expand each category's
 * `[default, floor]` to every minute-bucket so the whole matrix is populated here.
 */
export function defaultPricingRules(): ContentPricingRules {
  // Photos are set-priced: low .99 units + a bundle discount so a set of 8 nude photos lands
  // at $14.99 (not 8x a single). Videos are per-unit standalone PPVs. All cells end in .99;
  // bigdayz.new re-charms the final composed price and applies the $3 OnlyFans minimum at send.
  const photo: Record<string, PricePair> = {
    non_nude: [0, 0], // soft selfies may be sent free
    nude: [2.99, 1.99],
    explicit: [4.99, 2.99],
  };

  const videoDefault: Record<VideoCategory, PricePair> = {
    non_nude: [10.99, 6.99],
    life: [10.99, 6.99],
    striptease: [17.99, 11.99],
    solo: [19.99, 13.99],
    oral: [22.99, 15.99],
    masturbation: [24.99, 17.99],
    gg: [27.99, 19.99],
    bg: [27.99, 19.99],
    anal: [29.99, 21.99],
    hardcore: [29.99, 21.99],
  };
  const video: Record<string, Record<string, PricePair>> = {};
  for (const cat of VIDEO_CATEGORIES) {
    const cells: Record<string, PricePair> = {};
    for (const b of MINUTE_BUCKETS) cells[b] = [...videoDefault[cat]] as PricePair;
    video[cat] = cells;
  }

  // Bundle-size discount curve (mirrors bigdayz.new DEFAULT_PRICING_RULES): [threshold, mult].
  const bundle_discount: [number, number][] = [
    [1, 1.0], [2, 0.9], [3, 0.82], [5, 0.72], [8, 0.63], [10, 0.58],
  ];

  return { photo, video, bundle_discount, overrides: [] };
}

/**
 * A sensible starting matrix an operator can tweak. Free tiers (non_nude / life)
 * stay $0; paid archetypes scale by nudity and, for video, by length. Every cell
 * is a `[default, floor]` pair with the floor ~65% of the default.
 */
export function suggestedPricingRules(): ContentPricingRules {
  const pair = (d: number): PricePair => [d, Math.max(0, Math.round(d * 0.65)) || d];

  const photo: Record<string, PricePair> = {
    non_nude: [0, 0],
    nude: pair(12),
    explicit: pair(22),
  };

  // Per-category base price for a <1min clip; each further minute-bucket adds ~40%.
  const videoBase: Record<VideoCategory, number> = {
    non_nude: 0,
    life: 0,
    striptease: 12,
    solo: 15,
    oral: 18,
    masturbation: 20,
    gg: 28,
    bg: 30,
    anal: 35,
    hardcore: 40,
  };
  const video: Record<string, Record<string, PricePair>> = {};
  for (const cat of VIDEO_CATEGORIES) {
    const base = videoBase[cat];
    if (base <= 0) {
      video[cat] = { '<1': [0, 0] };
      continue;
    }
    const cells: Record<string, PricePair> = {};
    MINUTE_BUCKETS.forEach((b, i) => {
      const d = Math.round(base * (1 + 0.4 * i));
      cells[b] = pair(d);
    });
    video[cat] = cells;
  }

  return {
    photo,
    video,
    bundle_discount: [
      [3, 0.9],
      [5, 0.8],
    ],
    overrides: [],
  };
}

/* ── Pricing presets (ADR 0032 sibling) ──────────────────────────────────────
   Four named starting-point templates an operator applies to fill a blank matrix in
   one click. A preset is a one-shot FILL: its numbers become ordinary, freely-editable
   per-account values — nothing stays "linked" to the preset. Prices charm-end in 9;
   floors derive as ~60% of the default, also charm-ended. Photos price the nude tier =
   explicit tier (per the operator's decision) with non_nude always free; videos price
   one number per category applied to every length bucket (duration-agnostic), with
   `life` always free and `non_nude` free until the two paid tiers. */

export const PRESET_KEYS = ['budget', 'standard', 'premium', 'luxury'] as const;
export type PresetKey = (typeof PRESET_KEYS)[number];

export const PRESET_LABEL: Record<PresetKey, string> = {
  budget: 'Budget',
  standard: 'Standard',
  premium: 'Premium',
  luxury: 'Luxury',
};

/** Floor = ~60% of the default, charm-ended in 9 (e.g. 7.99 → 4.79, 0.49 → 0.29). */
function presetFloor(def: number): number {
  if (def <= 0) return 0;
  const tenths = Math.max(1, Math.round(def * 0.6 * 10)); // nearest 0.10
  return Math.round((tenths / 10 - 0.01) * 100) / 100; // → x.x9
}

// Column order matches PRESET_KEYS: [budget, standard, premium, luxury].
const PRESET_PHOTO_NUDE: Record<PresetKey, number> = {
  budget: 0.49, standard: 0.99, premium: 1.99, luxury: 2.99,
};
const PRESET_VIDEO: Record<VideoCategory, [number, number, number, number]> = {
  non_nude: [0, 0, 9.99, 14.99],
  life: [0, 0, 0, 0],
  striptease: [7.99, 11.99, 16.99, 24.99],
  solo: [9.99, 14.99, 19.99, 29.99],
  oral: [11.99, 16.99, 22.99, 34.99],
  masturbation: [12.99, 18.99, 24.99, 37.99],
  gg: [14.99, 21.99, 27.99, 42.99],
  bg: [14.99, 21.99, 27.99, 42.99],
  anal: [16.99, 24.99, 29.99, 49.99],
  hardcore: [16.99, 24.99, 29.99, 49.99],
};

/** Build a full matrix for a preset. Video prices are category-level (one price applied
 *  to every minute-bucket), mirroring how bigdayz.new derives defaults. */
export function presetPricingRules(key: PresetKey): ContentPricingRules {
  const i = PRESET_KEYS.indexOf(key);
  const pairOf = (d: number): PricePair => (d <= 0 ? [0, 0] : [d, presetFloor(d)]);

  const nude = PRESET_PHOTO_NUDE[key];
  const photo: Record<string, PricePair> = {
    non_nude: [0, 0], // soft selfies stay free in every preset
    nude: pairOf(nude),
    explicit: pairOf(nude), // nude tier = explicit tier (operator decision)
  };

  const video: Record<string, Record<string, PricePair>> = {};
  for (const cat of VIDEO_CATEGORIES) {
    const d = PRESET_VIDEO[cat][i] ?? 0;
    const cells: Record<string, PricePair> = {};
    for (const b of MINUTE_BUCKETS) cells[b] = [...pairOf(d)] as PricePair;
    video[cat] = cells;
  }

  // Reuse the canonical taper so a set of photos lands sensibly (mirrors DEFAULT_PRICING_RULES).
  const bundle_discount: [number, number][] = [
    [1, 1.0], [2, 0.9], [3, 0.82], [5, 0.72], [8, 0.63], [10, 0.58],
  ];

  return { photo, video, bundle_discount, overrides: [] };
}

/* ── Simplified pricing (main screen) ────────────────────────────────────────
   The operator authors a handful of numbers — one paid-photo price, a `[short, long]`
   asking-price range per paid video *tier*, and one global negotiation-floor percentage —
   plus a few **Standard bundles**. Those COMPILE DOWN to the full {@link ContentPricingRules}
   matrix (and count-shape overrides) the runtime already prices with; the raw inputs are
   round-tripped in `rules.simple` / `rules.shape_overrides` so the screen re-shows them.
   The detailed 220-cell matrix stays available behind "Advanced pricing settings". */

// Fixed, curated groupings of the video categories by explicitness (NOT operator-editable).
export const VIDEO_TIER_KEYS = ['softcore', 'explicit_solo', 'hardcore'] as const;

export const VIDEO_TIER_CATEGORIES: Record<VideoTierKey, VideoCategory[]> = {
  softcore: ['striptease', 'solo'],
  explicit_solo: ['oral', 'masturbation'],
  hardcore: ['gg', 'bg', 'anal', 'hardcore'],
};

// Categories that stay free (shown separately, priced $0 unless the operator edits Advanced).
export const FREE_VIDEO_CATEGORIES: VideoCategory[] = ['non_nude', 'life'];

function clampPct(n: number): number {
  if (!Number.isFinite(n)) return 65;
  return Math.min(100, Math.max(0, Math.round(n)));
}

/** Charm-round an asking price to end in .99 ($0 stays $0). e.g. 8 → 7.99, 18.4 → 17.99. */
function charmDefault(x: number | null | undefined): number {
  const v = x ?? 0;
  if (v <= 0) return 0;
  return Math.round((Math.round(v) - 0.01) * 100) / 100;
}

/** Floor = `pct`% of the (already-charmed) default, charm-ended in .99; ≥ 0. */
function deriveFloor(def: number, pct: number): number {
  if (def <= 0) return 0;
  const raw = def * (pct / 100);
  const tenths = Math.max(1, Math.round(raw * 10)); // nearest 0.10
  return Math.round((tenths / 10 - 0.01) * 100) / 100; // → x.x9
}

/** A `[default, floor]` pair for an asking price, or `[0,0]` when free. */
function paidPair(defRaw: number, pct: number): PricePair {
  const d = charmDefault(defRaw);
  return d > 0 ? [d, deriveFloor(d, pct)] : [0, 0];
}

/** Linear interpolation of a tier's `[short, long]` range across `n` length buckets. */
function lerpRange(range: TierRange, i: number, n: number): number {
  const s = range[0] ?? 0;
  const l = range[1] ?? range[0] ?? 0;
  if (n <= 1) return s;
  return s + (l - s) * (i / (n - 1));
}

/** Sensible starting inputs for a blank account. */
export function defaultSimpleInputs(): SimplePricingInputs {
  return {
    photo_price: 3,
    video: {
      softcore: [8, 18],
      explicit_solo: [12, 26],
      hardcore: [18, 40],
    },
    floor_pct: 65,
  };
}

/** The recommended Standard bundles, kept as defaults (operator-editable).
 *  `price` is `[asking, floor]` = `[high, low]` of the operator's stated range.
 *  A `null` shape dimension means that media must be ABSENT (count 0), so `videos: null`
 *  is a photos-only bundle and `photos: null` is a pure-video bundle. Small shapes
 *  (`photos [0,4]`, pure-video) exist because the auto-composition ladder builds small,
 *  video-leaning bundles that fell below the 5-photo floor of the original four and so
 *  reverted to the (expensive) summed category rate.
 *
 *  NOTE the `mix_*` shapes require **at least one photo** (`photos [1,4]`, not `[0,4]`): a
 *  "mix" flat price must not swallow a PURE single-video PPV. Since `price_bundle()` treats a
 *  shape override as the top lever (short-circuiting the per-length video grid), a mix with a
 *  zero photo floor would flat-price every lone-video send (esp. on legacy video-only ladders,
 *  where every rung is one video). With `[1,4]`, a pure single video matches no shape and
 *  correctly prices off the video-length grid. */
export function defaultStandardBundles(): ShapeContentOverride[] {
  return [
    { label: 'photos_tiny', shape: { photos: [0, 4], videos: null }, price: [6.99, 3.99] },
    { label: 'photos_small', shape: { photos: [5, 9], videos: null }, price: [9.99, 5.99] },
    { label: 'photos_large', shape: { photos: [10, 15], videos: null }, price: [12.99, 9.99] },
    { label: 'mix_tiny', shape: { photos: [1, 4], videos: [1, 3] }, price: [14.99, 8.99] },
    { label: 'mix_small', shape: { photos: [5, 9], videos: [1, 3] }, price: [15.99, 9.99] },
    { label: 'mix_large', shape: { photos: [10, 15], videos: [1, 3] }, price: [19.99, 14.99] },
    { label: 'videos_small', shape: { photos: null, videos: [2, 3] }, price: [19.99, 12.99] },
  ];
}

/** Compile the simplified inputs (+ Standard bundles) into the full pricing matrix.
 *  Photo tiers price nude = explicit = the single paid-photo price (non-nude free); each
 *  paid video tier's `[short, long]` range interpolates across the 11 length buckets for
 *  every member category; floors derive from the one global percentage. The account's
 *  Advanced homogeneous `overrides` and bundle-size `bundle_discount` are preserved. */
export function compileSimpleToRules(
  simple: SimplePricingInputs,
  bundles: ShapeContentOverride[],
  existing?: ContentPricingRules | null,
): ContentPricingRules {
  const pct = clampPct(simple.floor_pct);

  const photoPair = paidPair(simple.photo_price ?? 0, pct);
  const photo: Record<string, PricePair> = {
    non_nude: [0, 0],
    nude: photoPair,
    explicit: photoPair, // nude tier = explicit tier (single paid-photo price)
  };

  const video: Record<string, Record<string, PricePair>> = {};
  for (const cat of FREE_VIDEO_CATEGORIES) {
    const cells: Record<string, PricePair> = {};
    for (const b of MINUTE_BUCKETS) cells[b] = [0, 0];
    video[cat] = cells;
  }
  for (const tier of VIDEO_TIER_KEYS) {
    const range = simple.video[tier] ?? [0, 0];
    for (const cat of VIDEO_TIER_CATEGORIES[tier]) {
      const cells: Record<string, PricePair> = {};
      MINUTE_BUCKETS.forEach((b, i) => {
        cells[b] = paidPair(lerpRange(range, i, MINUTE_BUCKETS.length), pct);
      });
      video[cat] = cells;
    }
  }

  const bundle_discount = existing?.bundle_discount ?? defaultPricingRules().bundle_discount;
  const overrides = existing?.overrides ?? [];

  return {
    photo,
    video,
    bundle_discount,
    overrides,
    shape_overrides: bundles,
    simple: { ...simple, floor_pct: pct },
  };
}

/** Read the simplified inputs back: the stored `rules.simple` when present, else a
 *  best-effort reverse-derivation from an existing matrix (exact once re-saved). */
export function readSimpleInputs(rules: ContentPricingRules | null | undefined): SimplePricingInputs {
  if (rules?.simple) {
    const d = defaultSimpleInputs();
    const s = rules.simple;
    return {
      photo_price: s.photo_price ?? d.photo_price,
      video: { ...d.video, ...(s.video ?? {}) },
      floor_pct: clampPct(s.floor_pct ?? d.floor_pct),
    };
  }

  const d = defaultSimpleInputs();
  if (!hasAnyPricing(rules)) return d;

  const photo_price = rules!.photo?.explicit?.[0] ?? rules!.photo?.nude?.[0] ?? d.photo_price;
  const video: Record<VideoTierKey, TierRange> = { ...d.video };
  for (const tier of VIDEO_TIER_KEYS) {
    const cat = VIDEO_TIER_CATEGORIES[tier][0];
    const cells = rules!.video?.[cat];
    if (cells) {
      const short = cells['<1']?.[0] ?? null;
      const long = cells['>10']?.[0] ?? short;
      video[tier] = [short, long];
    }
  }

  let pct = d.floor_pct;
  const rep = rules!.photo?.explicit ?? rules!.video?.solo?.['<1'];
  if (rep && rep[0] && rep[1]) pct = clampPct((rep[1] / rep[0]) * 100);

  return { photo_price, video, floor_pct: pct };
}

/** Read the Standard bundles: the stored list when present (even if emptied), else the
 *  four recommended defaults so a fresh account starts with them. */
export function readStandardBundles(rules: ContentPricingRules | null | undefined): ShapeContentOverride[] {
  if (rules?.shape_overrides) return rules.shape_overrides;
  return defaultStandardBundles();
}
